"use client";

import { useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface MfaInputProps {
  /**
   * Callback when all 6 digits are entered
   */
  onComplete: (code: string) => void;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * External reset trigger - increment this to clear the input
   */
  resetTrigger?: number;
}

/**
 * MFA Input Component
 *
 * 6-digit code input with auto-advance between fields and auto-submit on completion.
 * Supports keyboard navigation, paste, and backspace.
 */
export function MfaInput({ onComplete, disabled = false, className, resetTrigger }: MfaInputProps) {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Reset when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined) {
      const id = setTimeout(() => {
        setCode(["", "", "", "", "", ""]);
        setHasSubmitted(false);
        inputRefs.current[0]?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [resetTrigger]);

  // Auto-submit when all 6 digits are entered (only once)
  useEffect(() => {
    const fullCode = code.join("");
    if (fullCode.length === 6 && /^\d{6}$/.test(fullCode) && !hasSubmitted) {
      const id = setTimeout(() => {
        setHasSubmitted(true);
        onComplete(fullCode);
      }, 0);
      return () => clearTimeout(id);
    }
  }, [code, onComplete, hasSubmitted]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "");

    if (digit.length === 0) {
      // Handle backspace
      const newCode = [...code];
      newCode[index] = "";
      setCode(newCode);
      return;
    }

    if (digit.length === 1) {
      // Single digit entered
      const newCode = [...code];
      newCode[index] = digit;
      setCode(newCode);

      // Auto-advance to next field
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (digit.length === 6) {
      // Handle paste of full code
      const newCode = digit.split("").slice(0, 6);
      setCode(newCode);
      inputRefs.current[5]?.focus();
    } else if (digit.length > 1) {
      // Handle paste starting from current field
      const remaining = 6 - index;
      const digits = digit.slice(0, remaining).split("");
      const newCode = [...code];
      digits.forEach((d, i) => {
        newCode[index + i] = d;
      });
      setCode(newCode);

      // Focus last filled field
      const lastIndex = Math.min(index + digits.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Backspace on empty field: move to previous field
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").replace(/\D/g, "");

    if (pasteData.length === 6) {
      const newCode = pasteData.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex gap-3 justify-center">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={6} // Allow paste of full code
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={handleFocus}
            onPaste={handlePaste}
            disabled={disabled}
            className={cn(
              "h-14 w-12 rounded-md border bg-background text-center text-xl font-semibold font-mono",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent",
              "disabled:cursor-not-allowed disabled:opacity-50",
              digit && "border-accent"
            )}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      <p className="text-xs text-center text-muted-foreground">
        Enter the 6-digit code from your authenticator app
      </p>
    </div>
  );
}
